import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Heart, ArrowRight, Building2 } from "lucide-react";

export default function Sponsors() {
  const [sponsors, setSponsors] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", organization: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/sponsors/public").then((r) => setSponsors(r.data || [])).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/contact", { kind: "sponsor", ...form });
      toast.success("Thanks! We'll be in touch soon.");
      setForm({ name: "", email: "", organization: "", phone: "", message: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <section className="max-w-7xl mx-auto px-5 md:px-10 pt-16 md:pt-24 pb-8">
        <span className="text-xs uppercase tracking-wider font-semibold text-[#FF9F1C]">Sponsors</span>
        <h1 className="mt-3 font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-none">Powered by neighbors who care.</h1>
        <p className="mt-6 text-lg text-[#7A7D8C] max-w-2xl">Local businesses, families, and friends of the club keep events affordable and camps well-stocked with snacks.</p>
      </section>

      {/* Intro block with wood-cube image + Contact Us CTA */}
      <section className="max-w-7xl mx-auto px-5 md:px-10 py-10 grid lg:grid-cols-2 gap-12 items-center">
        <div className="relative">
          <div className="absolute inset-0 -rotate-2 rounded-[40px] bg-[#FFBF69]/25" aria-hidden />
          <img
            src="/assets/cube_classic.png"
            alt="Colorful Rubik's cube resting on a wooden surface"
            className="relative rounded-[36px] w-full h-[480px] object-cover shadow-[0_20px_60px_rgb(0,0,0,0.1)]"
            data-testid="sponsors-intro-image"
          />
        </div>
        <div>
          <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tight">Sponsors</h2>
          <p className="mt-5 text-lg text-[#1E1E24] leading-relaxed max-w-lg">
            If your company is interested in sponsoring our Rubik's Cube competitions, please contact us. We would like to put your company's logo on our website below as one of "Our Sponsors."
          </p>
          <div className="mt-8">
            <Link
              to="/contact"
              data-testid="sponsors-contact-us-btn"
              className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-[#FF9F1C] text-white font-bold shadow-[0_12px_30px_rgb(255,159,28,0.35)] hover:-translate-y-0.5 transition-transform"
            >
              Contact Us <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 md:px-10 py-8">
        <h2 className="font-display font-black text-2xl tracking-tight mb-6">Current sponsors</h2>
        {sponsors.length === 0 ? (
          <div className="rounded-3xl border-2 border-dashed border-slate-200 p-12 text-center">
            <Heart className="w-8 h-8 mx-auto text-[#FF9F1C]" />
            <p className="mt-4 text-[#7A7D8C]">Your logo could be here! Scroll down to become one of our first sponsors.</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
            {sponsors.map((s) => (
              <a key={s.id} href={s.website || "#"} target="_blank" rel="noreferrer" className="bg-white rounded-3xl p-6 border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:-translate-y-1 hover:shadow-[0_20px_40px_rgb(255,159,28,0.15)] transition-transform">
                {s.logo_url ? (
                  <img src={s.logo_url} alt={s.name} className="h-16 w-full object-contain" />
                ) : (
                  <div className="h-16 grid place-items-center text-[#FF9F1C]"><Building2 className="w-8 h-8" /></div>
                )}
                <div className="mt-4 font-display font-bold">{s.name}</div>
                <div className="text-xs uppercase tracking-wider text-[#7A7D8C] mt-1">{s.tier}</div>
              </a>
            ))}
          </div>
        )}
      </section>

      <section className="max-w-7xl mx-auto px-5 md:px-10 py-16">
        <div className="rounded-[36px] bg-white border border-slate-100 p-8 md:p-12 shadow-[0_8px_30px_rgb(0,0,0,0.04)] grid lg:grid-cols-2 gap-10">
          <div>
            <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tight" data-testid="sponsor-section-heading">Become a Sponsor</h2>
            <ul className="mt-6 space-y-3 text-[#1E1E24]">
              <li className="flex gap-3"><span className="mt-1 w-2 h-2 rounded-full bg-[#FF9F1C]" /> Local visibility to youth cubing families</li>
              <li className="flex gap-3"><span className="mt-1 w-2 h-2 rounded-full bg-[#FF9F1C]" /> Recognition at all events and camps</li>
            </ul>
          </div>
          <form onSubmit={submit} className="space-y-4" data-testid="sponsor-inquiry-form">
            <Field label="Your name" value={form.name} required onChange={(v) => setForm({ ...form, name: v })} tid="sponsor-name" />
            <Field label="Company / organization" value={form.organization} onChange={(v) => setForm({ ...form, organization: v })} tid="sponsor-org" />
            <Field label="Email" type="email" value={form.email} required onChange={(v) => setForm({ ...form, email: v })} tid="sponsor-email" />
            <Field label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} tid="sponsor-phone" />
            <div>
              <label className="text-xs uppercase tracking-wider font-semibold text-[#7A7D8C]">Message</label>
              <textarea data-testid="sponsor-message" required rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-1 w-full rounded-xl border border-slate-200 bg-[#FDFFFC] px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF9F1C] focus:border-[#FF9F1C]" />
            </div>
            <button type="submit" disabled={submitting} data-testid="sponsor-submit" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#FF9F1C] text-white font-bold shadow-[0_12px_30px_rgb(255,159,28,0.35)] hover:-translate-y-0.5 transition-transform disabled:opacity-60">
              {submitting ? "Sending…" : "Send inquiry"} <ArrowRight className="w-4 h-4" />
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required, tid }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-wider font-semibold text-[#7A7D8C]">{label}</label>
      <input data-testid={tid} type={type} value={value} required={required} onChange={(e) => onChange(e.target.value)} className="mt-1 w-full rounded-xl border border-slate-200 bg-[#FDFFFC] px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF9F1C] focus:border-[#FF9F1C]" />
    </div>
  );
}
